import { useState, useEffect, useRef } from "react";
import DashboardLayout from "@/components/DashboardLayout";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Separator } from "@/components/ui/separator";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogDescription,
  DialogFooter
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { 
  Bot, Send, Plus, Trash2, Settings, Eye, Code, Database,
  Zap, Brain, Users, GitBranch, Loader2, CheckCircle, XCircle
} from "lucide-react";
import axios from "axios";
import { API } from "@/App";
import { toast } from "sonner";

export default function AIAgentChatPage() {
  const [agents, setAgents] = useState([]);
  const [selectedAgent, setSelectedAgent] = useState(null);
  const [messages, setMessages] = useState([]);
  const [inputMessage, setInputMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showMemoryPanel, setShowMemoryPanel] = useState(false);
  const [conversationMemory, setConversationMemory] = useState([]);
  const [multiAgentMode, setMultiAgentMode] = useState(false);
  const [selectedAgents, setSelectedAgents] = useState([]);
  const [communicationPattern, setCommunicationPattern] = useState("sequential");
  
  const messagesEndRef = useRef(null);

  // New agent form state
  const [newAgent, setNewAgent] = useState({
    name: "",
    provider: "openai",
    model: "gpt-4o",
    system_prompt: "",
    temperature: 0.7,
    max_tokens: 4096,
    api_key: "",
    tools: [],
    memory_enabled: true,
    max_memory_messages: 50
  });

  useEffect(() => {
    fetchAgents();
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  const fetchAgents = async () => {
    try {
      const response = await axios.get(`${API}/agents`, { withCredentials: true });
      setAgents(response.data);
      if (response.data.length > 0 && !selectedAgent) {
        setSelectedAgent(response.data[0]);
      }
    } catch (error) {
      console.error('Error fetching agents:', error);
      toast.error("Failed to load agents");
    }
  };

  const createAgent = async () => {
    try {
      const response = await axios.post(`${API}/agents`, newAgent, { withCredentials: true });
      toast.success(`Agent "${newAgent.name}" created!`);
      setShowCreateModal(false);
      fetchAgents();
      
      // Reset form
      setNewAgent({
        name: "",
        provider: "openai",
        model: "gpt-4o",
        system_prompt: "",
        temperature: 0.7,
        max_tokens: 4096,
        api_key: "",
        tools: [],
        memory_enabled: true,
        max_memory_messages: 50
      });
    } catch (error) {
      console.error('Error creating agent:', error);
      toast.error(error.response?.data?.error || "Failed to create agent");
    }
  };

  const sendMessage = async () => {
    if (!inputMessage.trim() || !selectedAgent) return;

    const userMessage = {
      id: `msg_${Date.now()}`,
      role: "user",
      content: inputMessage,
      timestamp: new Date().toISOString()
    };

    setMessages(prev => [...prev, userMessage]);
    setInputMessage("");
    setLoading(true);

    try {
      if (multiAgentMode && selectedAgents.length > 1) {
        // Multi-agent execution
        const response = await axios.post(`${API}/agents/multi-agent`, {
          pattern: communicationPattern,
          agent_ids: selectedAgents.map(a => a.agent_id),
          message: inputMessage,
          master_agent_id: communicationPattern === "hierarchical" ? selectedAgents[0].agent_id : null
        }, { withCredentials: true });

        const assistantMessage = {
          id: `msg_${Date.now()}_agent`,
          role: "assistant",
          content: response.data.final_output || response.data.merged_output || JSON.stringify(response.data),
          timestamp: new Date().toISOString(),
          multi_agent: true,
          pattern: response.data.pattern,
          results: response.data.results
        };

        setMessages(prev => [...prev, assistantMessage]);
      } else {
        // Single agent chat
        const response = await axios.post(`${API}/agents/${selectedAgent.agent_id}/chat`, {
          message: inputMessage,
          conversation_id: selectedAgent.conversation_id
        }, { withCredentials: true });

        const assistantMessage = {
          id: `msg_${Date.now()}_agent`,
          role: "assistant",
          content: response.data.response,
          timestamp: new Date().toISOString(),
          tool_calls: response.data.tool_calls,
          tool_results: response.data.tool_results,
          metadata: response.data.metadata
        };

        setMessages(prev => [...prev, assistantMessage]);
      }
    } catch (error) {
      console.error('Error sending message:', error);
      toast.error("Failed to send message");
      
      const errorMessage = {
        id: `msg_${Date.now()}_error`,
        role: "error",
        content: error.response?.data?.detail || "An error occurred",
        timestamp: new Date().toISOString()
      };
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setLoading(false);
    }
  };

  const loadMemory = async () => {
    if (!selectedAgent) return;

    try {
      const response = await axios.get(`${API}/agents/${selectedAgent.agent_id}/memory`, {
        withCredentials: true
      });
      setConversationMemory(response.data.conversations);
      setShowMemoryPanel(true);
    } catch (error) {
      console.error('Error loading memory:', error);
      toast.error("Failed to load memory");
    }
  };

  const deleteAgent = async (agentId) => {
    try {
      await axios.delete(`${API}/agents/${agentId}`, { withCredentials: true });
      toast.success("Agent deleted");
      fetchAgents();
      if (selectedAgent?.agent_id === agentId) {
        setSelectedAgent(null);
        setMessages([]);
      }
    } catch (error) {
      console.error('Error deleting agent:', error);
      toast.error("Failed to delete agent");
    }
  };

  const getProviderIcon = (provider) => {
    const icons = {
      openai: "🤖",
      claude: "🧠",
      gemini: "✨"
    };
    return icons[provider] || "🤖";
  };

  const getModelLabel = (provider, model) => {
    const labels = {
      "gpt-4o": "GPT-4o",
      "gpt-4o-mini": "GPT-4o Mini",
      "gpt-5.2": "GPT-5.2",
      "claude-sonnet-4": "Claude Sonnet 4",
      "claude-haiku": "Claude Haiku",
      "gemini-2.5-flash": "Gemini 2.5 Flash",
      "gemini-2.5-pro": "Gemini 2.5 Pro"
    };
    return labels[model] || model;
  };

  return (
    <DashboardLayout>
      <div className="h-[calc(100vh-80px)] flex gap-4" data-testid="ai-agent-chat-page">
        {/* Sidebar - Agent List */}
        <Card className="w-80 glass-card border-border flex flex-col">
          <CardHeader>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Bot className="w-5 h-5 text-primary" />
                <CardTitle className="font-heading text-lg">AI Agents</CardTitle>
              </div>
              <Button
                size="sm"
                onClick={() => setShowCreateModal(true)}
                data-testid="create-agent-btn"
              >
                <Plus className="w-4 h-4" />
              </Button>
            </div>
          </CardHeader>
          <CardContent className="flex-1 overflow-hidden p-0">
            <ScrollArea className="h-full px-4 pb-4">
              {agents.length === 0 ? (
                <div className="text-center py-12">
                  <Bot className="w-12 h-12 text-muted-foreground mx-auto mb-3 opacity-50" />
                  <p className="text-sm text-muted-foreground">No agents yet</p>
                  <p className="text-xs text-muted-foreground mt-1">
                    Create your first AI agent
                  </p>
                </div>
              ) : (
                <div className="space-y-2">
                  {agents.map((agent) => (
                    <div
                      key={agent.agent_id}
                      className={`p-3 rounded-lg cursor-pointer transition-all border ${
                        selectedAgent?.agent_id === agent.agent_id
                          ? 'bg-primary/10 border-primary/30'
                          : 'bg-muted/20 border-transparent hover:bg-muted/40'
                      }`}
                      onClick={() => {
                        setSelectedAgent(agent);
                        setMessages([]);
                        setMultiAgentMode(false);
                      }}
                      data-testid={`agent-${agent.agent_id}`}
                    >
                      <div className="flex items-start justify-between mb-2">
                        <div className="flex items-center gap-2">
                          <span className="text-xl">{getProviderIcon(agent.provider)}</span>
                          <div>
                            <p className="font-semibold text-sm text-foreground">{agent.name}</p>
                            <p className="text-xs text-muted-foreground">
                              {getModelLabel(agent.provider, agent.model)}
                            </p>
                          </div>
                        </div>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={(e) => {
                            e.stopPropagation();
                            deleteAgent(agent.agent_id);
                          }}
                          className="h-6 w-6 p-0"
                        >
                          <Trash2 className="w-3 h-3" />
                        </Button>
                      </div>
                      <div className="flex items-center gap-2">
                        <Badge variant="outline" className="text-xs">
                          {agent.provider}
                        </Badge>
                        {agent.memory_enabled && (
                          <Badge variant="outline" className="text-xs">
                            <Brain className="w-3 h-3 mr-1" />
                            Memory
                          </Badge>
                        )}
                        {agent.tools?.length > 0 && (
                          <Badge variant="outline" className="text-xs">
                            <Zap className="w-3 h-3 mr-1" />
                            {agent.tools.length}
                          </Badge>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </ScrollArea>
          </CardContent>
        </Card>

        {/* Main Chat Area */}
        <Card className="flex-1 glass-card border-border flex flex-col">
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                {selectedAgent ? (
                  <>
                    <CardTitle className="font-heading flex items-center gap-2">
                      <span>{getProviderIcon(selectedAgent.provider)}</span>
                      {selectedAgent.name}
                    </CardTitle>
                    <CardDescription>
                      {getModelLabel(selectedAgent.provider, selectedAgent.model)}
                      {multiAgentMode && ` • Multi-Agent (${communicationPattern})`}
                    </CardDescription>
                  </>
                ) : (
                  <CardTitle className="font-heading">Select an agent to start chatting</CardTitle>
                )}
              </div>
              {selectedAgent && (
                <div className="flex items-center gap-2">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={loadMemory}
                    data-testid="view-memory-btn"
                  >
                    <Eye className="w-4 h-4 mr-2" />
                    Memory
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => setMultiAgentMode(!multiAgentMode)}
                    data-testid="multi-agent-toggle"
                  >
                    <Users className="w-4 h-4 mr-2" />
                    {multiAgentMode ? "Single" : "Multi"}
                  </Button>
                </div>
              )}
            </div>
          </CardHeader>

          {multiAgentMode && (
            <div className="px-6 pb-4">
              <div className="flex items-center gap-4 p-3 bg-muted/30 rounded-lg">
                <Label className="text-sm font-medium">Pattern:</Label>
                <Select value={communicationPattern} onValueChange={setCommunicationPattern}>
                  <SelectTrigger className="w-40">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="sequential">Sequential →</SelectItem>
                    <SelectItem value="parallel">Parallel ⚡</SelectItem>
                    <SelectItem value="hierarchical">Hierarchical 🎯</SelectItem>
                  </SelectContent>
                </Select>
                <Label className="text-sm font-medium">Agents:</Label>
                <div className="flex gap-2 flex-wrap">
                  {agents.map((agent) => (
                    <Badge
                      key={agent.agent_id}
                      variant={selectedAgents.find(a => a.agent_id === agent.agent_id) ? "default" : "outline"}
                      className="cursor-pointer"
                      onClick={() => {
                        setSelectedAgents(prev =>
                          prev.find(a => a.agent_id === agent.agent_id)
                            ? prev.filter(a => a.agent_id !== agent.agent_id)
                            : [...prev, agent]
                        );
                      }}
                    >
                      {agent.name}
                    </Badge>
                  ))}
                </div>
              </div>
            </div>
          )}

          <Separator />

          {/* Messages Area */}
          <CardContent className="flex-1 overflow-hidden p-0">
            <ScrollArea className="h-full px-6 py-4">
              {messages.length === 0 ? (
                <div className="flex items-center justify-center h-full">
                  <div className="text-center">
                    <Bot className="w-16 h-16 text-muted-foreground mx-auto mb-4 opacity-50" />
                    <p className="text-lg font-medium text-foreground">Start a conversation</p>
                    <p className="text-sm text-muted-foreground mt-2">
                      Type a message below to chat with your AI agent
                    </p>
                  </div>
                </div>
              ) : (
                <div className="space-y-4">
                  {messages.map((msg) => (
                    <div
                      key={msg.id}
                      className={`flex gap-3 ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
                    >
                      {msg.role !== 'user' && (
                        <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                          <Bot className="w-5 h-5 text-primary" />
                        </div>
                      )}
                      <div className={`max-w-[70%] ${msg.role === 'user' ? 'order-1' : ''}`}>
                        <div
                          className={`p-4 rounded-2xl ${
                            msg.role === 'user'
                              ? 'bg-primary text-primary-foreground'
                              : msg.role === 'error'
                              ? 'bg-red-500/10 border border-red-500/20'
                              : 'bg-muted'
                          }`}
                        >
                          <p className="text-sm whitespace-pre-wrap">{msg.content}</p>
                          
                          {/* Tool Calls Visualization */}
                          {msg.tool_calls && msg.tool_calls.length > 0 && (
                            <div className="mt-3 pt-3 border-t border-border/50">
                              <p className="text-xs font-medium text-muted-foreground mb-2 flex items-center gap-1">
                                <Zap className="w-3 h-3" />
                                Tool Calls ({msg.tool_calls.length})
                              </p>
                              {msg.tool_calls.map((tc, i) => (
                                <details key={i} className="mb-2">
                                  <summary className="text-xs cursor-pointer hover:text-foreground text-muted-foreground">
                                    {tc.name}
                                  </summary>
                                  <pre className="text-xs mt-1 p-2 bg-background/50 rounded overflow-x-auto">
                                    {JSON.stringify(tc.arguments, null, 2)}
                                  </pre>
                                </details>
                              ))}
                            </div>
                          )}

                          {/* Tool Results */}
                          {msg.tool_results && msg.tool_results.length > 0 && (
                            <div className="mt-2 space-y-1">
                              {msg.tool_results.map((tr, i) => (
                                <div key={i} className="text-xs flex items-center gap-2">
                                  {tr.success ? (
                                    <CheckCircle className="w-3 h-3 text-green-500" />
                                  ) : (
                                    <XCircle className="w-3 h-3 text-red-500" />
                                  )}
                                  <span className="text-muted-foreground">{tr.tool_name}</span>
                                </div>
                              ))}
                            </div>
                          )}

                          {/* Multi-Agent Results */}
                          {msg.multi_agent && msg.results && (
                            <div className="mt-3 pt-3 border-t border-border/50">
                              <p className="text-xs font-medium text-muted-foreground mb-2 flex items-center gap-1">
                                <Users className="w-3 h-3" />
                                Agent Results ({msg.pattern})
                              </p>
                              {msg.results.map((result, i) => (
                                <details key={i} className="mb-2">
                                  <summary className="text-xs cursor-pointer hover:text-foreground text-muted-foreground">
                                    {result.agent_name}
                                  </summary>
                                  <div className="text-xs mt-1 p-2 bg-background/50 rounded">
                                    {result.response || result.error}
                                  </div>
                                </details>
                              ))}
                            </div>
                          )}
                        </div>
                        <p className="text-xs text-muted-foreground mt-1 px-2">
                          {new Date(msg.timestamp).toLocaleTimeString()}
                        </p>
                      </div>
                      {msg.role === 'user' && (
                        <div className="w-8 h-8 rounded-full bg-primary flex items-center justify-center flex-shrink-0 order-2">
                          <span className="text-sm text-primary-foreground font-medium">U</span>
                        </div>
                      )}
                    </div>
                  ))}
                  {loading && (
                    <div className="flex gap-3">
                      <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center">
                        <Loader2 className="w-5 h-5 text-primary animate-spin" />
                      </div>
                      <div className="p-4 rounded-2xl bg-muted">
                        <p className="text-sm text-muted-foreground">Thinking...</p>
                      </div>
                    </div>
                  )}
                  <div ref={messagesEndRef} />
                </div>
              )}
            </ScrollArea>
          </CardContent>

          {/* Input Area */}
          {selectedAgent && (
            <>
              <Separator />
              <div className="p-4">
                <div className="flex gap-2">
                  <Input
                    placeholder="Type your message..."
                    value={inputMessage}
                    onChange={(e) => setInputMessage(e.target.value)}
                    onKeyPress={(e) => {
                      if (e.key === 'Enter' && !e.shiftKey) {
                        e.preventDefault();
                        sendMessage();
                      }
                    }}
                    disabled={loading}
                    data-testid="chat-input"
                    className="flex-1"
                  />
                  <Button
                    onClick={sendMessage}
                    disabled={loading || !inputMessage.trim()}
                    data-testid="send-btn"
                  >
                    {loading ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <Send className="w-4 h-4" />
                    )}
                  </Button>
                </div>
              </div>
            </>
          )}
        </Card>
      </div>

      {/* Create Agent Modal */}
      <Dialog open={showCreateModal} onOpenChange={setShowCreateModal}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading">Create AI Agent</DialogTitle>
            <DialogDescription>
              Configure your AI agent with custom settings and tools
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="name">Agent Name</Label>
                <Input
                  id="name"
                  placeholder="My Assistant"
                  value={newAgent.name}
                  onChange={(e) => setNewAgent({...newAgent, name: e.target.value})}
                  data-testid="agent-name-input"
                />
              </div>
              <div>
                <Label htmlFor="provider">LLM Provider</Label>
                <Select
                  value={newAgent.provider}
                  onValueChange={(value) => setNewAgent({...newAgent, provider: value})}
                >
                  <SelectTrigger id="provider">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="openai">🤖 OpenAI</SelectItem>
                    <SelectItem value="claude">🧠 Claude (Anthropic)</SelectItem>
                    <SelectItem value="gemini">✨ Gemini (Google)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div>
              <Label htmlFor="model">Model</Label>
              <Select
                value={newAgent.model}
                onValueChange={(value) => setNewAgent({...newAgent, model: value})}
              >
                <SelectTrigger id="model">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {newAgent.provider === "openai" && (
                    <>
                      <SelectItem value="gpt-4o">GPT-4o</SelectItem>
                      <SelectItem value="gpt-4o-mini">GPT-4o Mini</SelectItem>
                      <SelectItem value="gpt-5.2">GPT-5.2</SelectItem>
                    </>
                  )}
                  {newAgent.provider === "claude" && (
                    <>
                      <SelectItem value="claude-sonnet-4">Claude Sonnet 4</SelectItem>
                      <SelectItem value="claude-haiku">Claude Haiku</SelectItem>
                    </>
                  )}
                  {newAgent.provider === "gemini" && (
                    <>
                      <SelectItem value="gemini-2.5-flash">Gemini 2.5 Flash</SelectItem>
                      <SelectItem value="gemini-2.5-pro">Gemini 2.5 Pro</SelectItem>
                    </>
                  )}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label htmlFor="system_prompt">System Prompt</Label>
              <Textarea
                id="system_prompt"
                placeholder="You are a helpful AI assistant..."
                value={newAgent.system_prompt}
                onChange={(e) => setNewAgent({...newAgent, system_prompt: e.target.value})}
                rows={4}
                data-testid="system-prompt-input"
              />
            </div>

            <div>
              <Label htmlFor="api_key">API Key (optional - leave empty to use default)</Label>
              <Input
                id="api_key"
                type="password"
                placeholder="sk-..."
                value={newAgent.api_key}
                onChange={(e) => setNewAgent({...newAgent, api_key: e.target.value})}
                data-testid="api-key-input"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="temperature">Temperature ({newAgent.temperature})</Label>
                <input
                  id="temperature"
                  type="range"
                  min="0"
                  max="2"
                  step="0.1"
                  value={newAgent.temperature}
                  onChange={(e) => setNewAgent({...newAgent, temperature: parseFloat(e.target.value)})}
                  className="w-full"
                />
              </div>
              <div>
                <Label htmlFor="max_tokens">Max Tokens</Label>
                <Input
                  id="max_tokens"
                  type="number"
                  value={newAgent.max_tokens}
                  onChange={(e) => setNewAgent({...newAgent, max_tokens: parseInt(e.target.value)})}
                />
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateModal(false)}>
              Cancel
            </Button>
            <Button
              onClick={createAgent}
              disabled={!newAgent.name || !newAgent.system_prompt}
              data-testid="create-agent-submit"
            >
              Create Agent
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Memory Panel Modal */}
      <Dialog open={showMemoryPanel} onOpenChange={setShowMemoryPanel}>
        <DialogContent className="max-w-4xl max-h-[90vh]">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2">
              <Brain className="w-5 h-5" />
              Agent Memory
            </DialogTitle>
            <DialogDescription>
              View conversation history and memory for {selectedAgent?.name}
            </DialogDescription>
          </DialogHeader>

          <ScrollArea className="h-[600px] pr-4">
            {conversationMemory.length === 0 ? (
              <div className="text-center py-12">
                <Brain className="w-12 h-12 text-muted-foreground mx-auto mb-3 opacity-50" />
                <p className="text-sm text-muted-foreground">No memory data yet</p>
              </div>
            ) : (
              <div className="space-y-4">
                {conversationMemory.map((conv, i) => (
                  <Card key={i} className="p-4">
                    <div className="flex items-start justify-between mb-3">
                      <div>
                        <p className="text-sm font-medium text-foreground">
                          Conversation {i + 1}
                        </p>
                        <p className="text-xs text-muted-foreground">
                          {new Date(conv.created_at).toLocaleString()}
                        </p>
                      </div>
                      {conv.metadata && (
                        <Badge variant="outline">
                          {conv.metadata.model}
                        </Badge>
                      )}
                    </div>
                    
                    <div className="space-y-2">
                      <div className="p-2 bg-muted/30 rounded">
                        <p className="text-xs font-medium text-muted-foreground mb-1">User:</p>
                        <p className="text-sm">{conv.user_message}</p>
                      </div>
                      <div className="p-2 bg-primary/5 rounded">
                        <p className="text-xs font-medium text-muted-foreground mb-1">Agent:</p>
                        <p className="text-sm">{conv.agent_response}</p>
                      </div>
                      
                      {conv.tool_calls && conv.tool_calls.length > 0 && (
                        <details className="text-xs">
                          <summary className="cursor-pointer text-muted-foreground hover:text-foreground">
                            Tool Calls ({conv.tool_calls.length})
                          </summary>
                          <pre className="mt-2 p-2 bg-muted rounded overflow-x-auto">
                            {JSON.stringify(conv.tool_calls, null, 2)}
                          </pre>
                        </details>
                      )}
                    </div>
                  </Card>
                ))}
              </div>
            )}
          </ScrollArea>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
}
